import React, {useState} from 'react';
import {AddRounded, KeyboardArrowDown, SearchRounded} from '@mui/icons-material';
import {Box, Button, FormControlLabel, InputAdornment, Menu, MenuItem, Pagination, Switch, TextField, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import FormButton, {FormButtonProps} from '../../form/form_inputs/form_button';

export interface ListButtonsProps<T> {
    appendable?: boolean;
    actions?: FormButtonProps[];
    onClick: string | ((bean: T) => void);
    totalPages?: number;
    setCurrentPage: (n: number) => void;
    currentPage?: number;
    onAppend?: () => void;
    searchable?: boolean;
    term?: string;
    setTerm?: (term: string) => void;
    showDismissed?: boolean;
    setShowDismissed?: (v: boolean) => void;
}

function ActionGroupButton({group, items}: { group: string; items: FormButtonProps[] }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    return (
        <>
            <Button
                variant="contained"
                color="primary"
                endIcon={<KeyboardArrowDown/>}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{marginBottom: 1, marginRight: 1}}
            >
                {group}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {items.map((item, i) => (
                    <MenuItem key={i} onClick={() => {
                        setAnchorEl(null);
                        item.action();
                    }}>
                        {item.icon && <Box component="span" sx={{mr: 1, display: "inline-flex", verticalAlign: "middle"}}>{item.icon}</Box>}
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function ListButtons<T>({
                            appendable,
                            actions,
                            onClick,
                            totalPages,
                            currentPage,
                            onAppend,
                            setCurrentPage,
                            searchable,
                            term,
                            setTerm,
                            showDismissed,
                            setShowDismissed,
                        }: ListButtonsProps<T>) {
    const navigate = useNavigate();

    const groupMap = new Map<string, FormButtonProps[]>();
    const ungrouped: FormButtonProps[] = [];
    (actions ?? []).forEach(a => {
        if (a.group) {
            const arr = groupMap.get(a.group) ?? [];
            arr.push(a);
            groupMap.set(a.group, arr);
        } else {
            ungrouped.push(a);
        }
    });

    const hasButtons = appendable || ungrouped.length > 0 || groupMap.size > 0;

    return (
        <Box mb={2} display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            {hasButtons && (
                <Box display="flex" flexWrap="wrap" alignItems="center">
                    {appendable && (
                        <FormButton
                            color={"primary"}
                            label={"Añadir"}
                            icon={<AddRounded/>}
                            action={() => {
                                if (typeof onClick == "function") {
                                    if (onAppend) {
                                        onAppend();
                                    } else {
                                        alert("If onClick is a function, then you must provide a onAppend function");
                                    }
                                } else {
                                    navigate(`${onClick}/new`);
                                }
                            }}
                        />
                    )}
                    {[...groupMap.entries()].map(([group, items]) => (
                        <ActionGroupButton key={group} group={group} items={items}/>
                    ))}
                    {ungrouped.map((action, i) => (
                        <FormButton
                            key={"act-btn-" + i}
                            color={action.color}
                            label={action.label}
                            icon={action.icon}
                            action={action.action}
                        />
                    ))}
                </Box>
            )}

            <Box flex={1}/>

            {searchable && (
                <>
                    <FormControlLabel sx={{mr: 0, ml: 0}}
                                      control={<Switch size="small" checked={showDismissed ?? false}
                                                       onChange={(e) => setShowDismissed?.(e.target.checked)}/>}
                                      label={<Typography variant="body2" color="text.secondary">Eliminados</Typography>}/>
                    <TextField
                        placeholder="Buscar"
                        size="small"
                        value={term ?? ""}
                        onChange={(e) => setTerm?.(e.target.value)}
                        sx={{width: {xs: "100%", sm: 240}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}
                        InputProps={{startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small"/></InputAdornment>}}
                    />
                </>
            )}

            <Pagination
                showFirstButton showLastButton
                shape="rounded"
                size="small"
                page={(currentPage ?? 0) + 1}
                count={totalPages}
                color="primary"
                onChange={(_, pageNumber) => setCurrentPage(pageNumber - 1)}
            />
        </Box>
    );
}

export default ListButtons;
